import { Link } from "react-router-dom";
import { useState } from "react";
import { useHandoverSession } from "../../hooks/useHandoverSession";
import { api, formatApiError } from "../../lib/api";
import {
  Compass,
  Coins,
  ClipboardCheck,
  ArrowUpRight,
  Sparkles,
  Download,
  GraduationCap,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Info,
  Scale,
  UserCircle2,
  FileText,
  ChevronRight,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import {
  WorkspaceShell,
  WorkspaceSection,
  WorkspaceCard,
  StartHereCard,
  WorkspaceSupportBlock,
} from "../../components/workspace/WorkspaceShell";
import { NATIVE_FORM_IDS, nativeFormPath } from "../../data/nativeFormTemplateIds";

const UPGRADE_URL = "/membership/pro-carer-circle";

// Starter templates — preserve every title/body exactly as supplied; only
// the destination (href) changed from Drive to the native JCC form.
const TEMPLATES = [
  {
    n: 1,
    title: "Client Enquiry Form",
    body:
      "Use this form before you agree to start any placement. It helps you gather the essential information you need to work safely and professionally — medical details, emergency contacts, mobility needs and agreed rates. Completing this before day one prevents misunderstandings, protects you from assumptions, and ensures you are stepping into a placement informed, prepared and confident.",
    cta: "Open Client Enquiry Form",
    href: "/forms/f2b90506-7cba-407f-b93c-fbe9232caff8",
    internal: true,
  },
  {
    n: 2,
    title: "Basic Service Agreement",
    body:
      "This simple agreement should be completed and signed before work begins. It confirms what support you are providing, your hours, your rate, and how payment will be made. Even if the client is a friend, neighbour or “lovely family,” written clarity protects both sides. It prevents awkward conversations later and sets the tone that you are a professional self-employed carer running a business, not doing casual favours.",
    cta: "Download Basic Service Agreement (PDF)",
    href: nativeFormPath(NATIVE_FORM_IDS.BASIC_SERVICE_AGREEMENT),
    internal: true,
  },
  {
    n: 3,
    title: "Simple Invoice Template",
    body:
      "Send an invoice at the agreed interval (weekly, fortnightly or monthly). Always number your invoices and keep copies for your records. This creates a clear paper trail for tax purposes and reinforces that your service has structure and value. Never rely on verbal reminders for payment — invoicing keeps things professional and avoids blurred boundaries.",
    cta: "Download Simple Invoice Template (PDF)",
    href: nativeFormPath(NATIVE_FORM_IDS.SIMPLE_INVOICE_TEMPLATE),
    internal: true,
  },
  {
    n: 4,
    title: "Daily Care Notes",
    body:
      "Complete this at the end of each shift (or during, if appropriate). Keep entries factual, clear and free from emotion or opinion. These notes protect you if concerns arise, provide continuity of care and demonstrate professionalism. If something significant changes in the client’s condition, document it here and follow appropriate medical or family communication procedures.",
    cta: "Download Daily Care Notes (PDF)",
    href: nativeFormPath(NATIVE_FORM_IDS.DAILY_CARE_NOTES),
    internal: true,
  },
  {
    n: 5,
    title: "Basic Emergency Information Form",
    body:
      "Use this Basic Emergency Information Sheet to keep the most important details in one easy-to-find place. Complete it at the start of a placement with the client or family, check that all contact and medical information is accurate, and keep a copy somewhere accessible. It supports clear communication in an emergency — it does not replace professional medical advice or emergency services.",
    cta: "Download Emergency Information Form (PDF)",
    href: nativeFormPath(NATIVE_FORM_IDS.BASIC_EMERGENCY_INFO_FORM),
    internal: true,
  },
  {
    n: 6,
    title: "Self-Employed Carer Checklist",
    body: "Being organised from the start will save you headaches later. A simple checklist to keep your independent setup on track.",
    cta: "Download Self-Employed Carer Checklist (PDF)",
    href: nativeFormPath(NATIVE_FORM_IDS.SELF_EMPLOYED_CHECKLIST),
    internal: true,
  },
];

const CORE_TRAINING = [
  "Safeguarding Adults (usually Level 2)",
  "Medication Awareness",
  "Moving & Handling (Theory)",
  "Infection Prevention & Control",
  "Food Hygiene / Food Safety (Level 2)",
  "Dementia Awareness",
];

const TRAINING_STEPS = [
  {
    n: 1,
    title: "Choose a training provider",
    body:
      "Select a reputable, accredited training provider from the list below. Most offer individual courses, discounted bundles, and annual access options.",
  },
  {
    n: 2,
    title: "Register & purchase",
    body:
      "Create an account on the provider’s website and purchase the course(s) you need. You do not need to go through JCC — training is completed directly with the provider.",
  },
  {
    n: 3,
    title: "Complete the course online",
    body:
      "Courses are usually self-paced, made up of short modules, and accessible on phone, tablet or laptop — so you can fit them around shifts.",
  },
  {
    n: 4,
    title: "Download your certificate",
    body:
      "Once you’re done, you’ll receive a digital certificate (course name, completion date, CPD hours/level, provider). Save a copy for your records.",
  },
  {
    n: 5,
    title: "Keep certificates accessible",
    body:
      "Families, councils, insurers or agencies may ask to see your training. Store certificates safely, label them clearly, and refresh key courses regularly.",
  },
];

const PROVIDERS = [
  {
    name: "Florence Academy",
    body: "Clear, modern online courses designed for carers and health professionals. Commonly used for Medication Awareness, Safeguarding, Infection Control and Dementia Awareness. CPD-accredited, easy-to-download certificates, good for refresher training.",
  },
  {
    name: "Caredemy",
    body: "Affordable online training with course bundles and annual access options. Commonly used for core care courses, CPD top-ups and certificate refreshers. Budget-friendly, instant certification, suitable for self-employed carers.",
  },
  {
    name: "Social Care TV",
    body: "A long-established provider widely recognised in adult social care. Commonly used for core compliance courses, Safeguarding and Care Certificate theory modules. Well recognised, broad course library, suitable for professional portfolios.",
  },
  {
    name: "Redcrier Training Solutions",
    body: "Offers online and blended learning options across health and social care. Commonly used for Safeguarding, care practice theory and specialist awareness courses. Quality-focused, suitable for ongoing professional development.",
  },
  {
    name: "Skills for Health",
    body: "National organisation supporting workforce development in health and care. Commonly used for Care Certificate standards and framework-based learning. Recognised across the sector and a good reference point for standards.",
  },
  {
    name: "eLearning for Healthcare (NHS)",
    body: "Free NHS-supported learning platform. Commonly used for health awareness, professional development and knowledge refreshers. Free to access, excellent background learning, complements paid training.",
  },
];

const STARTER_PATH = [
  {
    n: 1,
    title: "Start with core training",
    body:
      "Begin with the most commonly expected courses (see the list above). You don’t need to complete everything at once. Many carers start with Safeguarding and Medication Awareness, then build from there.",
  },
  {
    n: 2,
    title: "Choose one provider",
    body: "Pick one accredited provider and complete a small bundle rather than jumping between sites. It keeps certificates consistent, records easy to manage, and costs predictable.",
  },
  {
    n: 3,
    title: "Keep certificates organised",
    body: "Download each certificate, save them clearly (e.g. “Safeguarding – 2026”), and keep them accessible in case a family asks.",
  },
  {
    n: 4,
    title: "Match training to the care you provide",
    body: "Add specialist training when your work calls for it — for example dementia or memory loss, Parkinson’s, stroke recovery or complex needs.",
  },
  {
    n: 5,
    title: "Build gradually",
    body: "Training isn’t a one-off task. Many experienced carers add one or two courses each year and refresh key training every 12–24 months.",
  },
];

const Para = ({ children }) => (
  <p className="text-ink-muted leading-relaxed">{children}</p>
);
const H3 = ({ children }) => (
  <h3 className="font-display text-lg md:text-xl font-semibold text-ink mt-6 first:mt-0">
    {children}
  </h3>
);
const Bullets = ({ items }) => (
  <ul className="mt-3 space-y-2">
    {items.map((it) => (
      <li key={it} className="flex gap-3 text-ink-muted leading-relaxed">
        <CheckCircle2
          className="w-5 h-5 mt-0.5 shrink-0 text-teal"
          strokeWidth={1.75}
          aria-hidden="true"
        />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function CarerStarterHub() {
  const { entitlements } = useHandoverSession();
  const hasCarerEssentials = entitlements.includes("carer_essentials") || entitlements.includes("pro_carer");
  const [billingState, setBillingState] = useState({ loading: false, error: "" });

  const openBillingPortal = async () => {
    setBillingState({ loading: true, error: "" });
    try {
      const { data } = await api.post("/api/membership/portal", {
        return_url: window.location.href,
      });
      window.location.assign(data.url);
    } catch (err) {
      setBillingState({
        loading: false,
        error:
          err.response?.status === 404
            ? "No native billing subscription found for this account — if you joined via Pro Carer Office, manage billing there instead."
            : formatApiError(err.response?.data?.detail) || err.message,
      });
    }
  };

  return (
    <WorkspaceShell
      testId="hub-carer-starter"
      eyebrow="Carer Starter Hub"
      memberBadge="Free Carer member"
      accent="teal"
      title="Welcome to your Just Care Community Free site."
      intro="You’re now part of Just Care Community as a Free Carer member. This is your personal space — a place to access guidance, understand how independent care works, and take your next steps at your own pace."
    >
      {/* ── JCC Forms Library quick-access ───────────────────────────────── */}
      <Link
        to="/forms"
        data-testid="carer-starter-forms-library-banner"
        className="group flex items-center justify-between gap-4 rounded-2xl border border-teal/20 bg-teal/5 p-5 hover:border-teal transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal/10 text-teal flex items-center justify-center shrink-0">
            <FileText className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div>
            <h3 className="font-display font-semibold">JCC Forms Library</h3>
            <p className="text-sm text-ink-muted">Complete, save and print your interactive JCC forms.</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-teal shrink-0 group-hover:translate-x-0.5 transition-transform" />
      </Link>

      {/* What you can do here */}
      <section className="rounded-3xl border border-slate-200 bg-white p-6 md:p-9" data-testid="carer-hub-what-you-can-do">
        <div className="grid md:grid-cols-3 gap-6 md:gap-10">
          <div>
            <div className="overline text-teal mb-2">Your membership level</div>
            <H3>Current plan: Free Carer</H3>
            <Para>You can upgrade at any time if and when it suits you.</Para>
          </div>
          <div>
            <div className="overline text-teal mb-2">What you can do here</div>
            <H3>Access free guidance &amp; resources</H3>
            <Para>Learn how private, self-employed care works — clearly and ethically.</Para>
          </div>
          <div>
            <div className="overline text-teal mb-2">Next steps</div>
            <H3>Understand your next steps</H3>
            <Para>Not sure what comes next? No problem — we’re here to help.</Para>
          </div>
        </div>
        <div className="mt-7 pt-6 border-t border-slate-100">
          <div className="overline text-ink-soft mb-3">In your resource section you will find</div>
          <Bullets
            items={[
              "How carers move from agency work to independence",
              "What to consider before taking private clients",
              "How to protect yourself from non-payment",
              "Basic care templates for everyday use",
            ]}
          />
        </div>
      </section>

      {/* Featured Free Tool — Independent Status Reality Check */}
      <section
        className="rounded-3xl border-2 border-teal/25 bg-white p-6 md:p-9"
        data-testid="carer-hub-featured-reality-check"
      >
        <div className="flex flex-col md:flex-row md:items-start gap-6">
          <div className="w-14 h-14 rounded-2xl bg-teal/10 text-teal flex items-center justify-center shrink-0">
            <Scale className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <span className="pill bg-teal/10 text-teal">Featured free tool</span>
            <h3 className="font-display text-xl md:text-2xl font-semibold mt-3">
              Independent Status Reality Check
            </h3>
            <p className="mt-1 text-teal font-medium">
              Does your care arrangement genuinely support self-employment?
            </p>
            <p className="mt-3 text-ink-muted leading-relaxed">
              Calling an arrangement &ldquo;self-employed&rdquo; does not automatically make it so. Answer
              a short series of questions about control, choice, other clients, substitution, payment and
              responsibility.
            </p>
            <Link
              to="/independent-status-reality-check"
              className="btn-teal mt-5"
              data-testid="carer-hub-reality-check-cta"
            >
              Start the Reality Check <ArrowUpRight className="w-4 h-4" />
            </Link>
            <p className="mt-4 text-sm text-ink-muted">
              Free to use &middot; Takes around 3&ndash;5 minutes &middot; Answers are not stored
            </p>
            <p className="mt-1 text-xs text-ink-soft">
              This tool provides general information and cannot determine your legal employment status.
            </p>
          </div>
        </div>
      </section>

      {/* Browse Care Opportunities — every registered carer can browse
          AND express interest for free; no paid tier required. */}
      <section
        className="rounded-3xl border border-slate-200 bg-white p-6 md:p-9"
        data-testid="carer-hub-care-opportunities"
      >
        <div className="flex flex-col md:flex-row md:items-start gap-6">
          <div className="w-14 h-14 rounded-2xl bg-teal/10 text-teal flex items-center justify-center shrink-0">
            <Compass className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <span className="pill bg-teal/10 text-teal">Care Opportunities</span>
            <h3 className="font-display text-xl md:text-2xl font-semibold mt-3">
              Browse Care Opportunities
            </h3>
            <p className="mt-3 text-ink-muted leading-relaxed">
              See published Care Needs from families looking for independent care, express your interest and hear
              back from families directly — free for every registered JCC Carer, no paid membership required.
            </p>
            <Link
              to="/workspace/pro-carer-circle/care-opportunities"
              className="btn-teal mt-5"
              data-testid="carer-hub-care-opportunities-cta"
            >
              Browse Care Opportunities <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Carer Profile entry point — free for every registered carer to
          create, edit and publish. Carer Essentials/billing are only
          shown as an optional add-on for those who already hold them. */}
      <section
        className="rounded-3xl border-2 border-purple/25 bg-white p-6 md:p-9"
        data-testid="carer-hub-manage-carer-profile"
      >
        <div className="flex flex-col md:flex-row md:items-start gap-6">
          <div className="w-14 h-14 rounded-2xl bg-purple/10 text-purple flex items-center justify-center shrink-0">
            <UserCircle2 className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <span className="pill bg-purple/10 text-purple">Carer Profile</span>
            <h3 className="font-display text-xl md:text-2xl font-semibold mt-3">
              Create your free Carer Profile
            </h3>
            <p className="mt-3 text-ink-muted leading-relaxed">
              Publish a profile families can find directly — your photo, about you, care experience, availability
              and basic checks, reviewed and approved by JCC before it goes live. Free for every registered JCC
              Carer: no payment is needed to create, edit, publish or keep your profile visible, browse Care
              Needs, express interest, or receive and respond to enquiries and invitations.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/workspace/pro-carer-profile" className="btn-purple" data-testid="carer-hub-open-profile-cta">
                Open my Carer Profile <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/workspace/carer-profile/enquiries" className="btn-outline" data-testid="carer-hub-open-enquiries-cta">
                Profile Enquiries & Invitations
              </Link>
              {hasCarerEssentials && (
                <>
                  <Link to="/workspace/carer-essentials" className="btn-outline" data-testid="carer-hub-open-essentials-cta">
                    Carer Essentials
                  </Link>
                  <button
                    type="button"
                    onClick={openBillingPortal}
                    disabled={billingState.loading}
                    className="btn-outline"
                    data-testid="carer-hub-manage-billing-cta"
                  >
                    {billingState.loading ? "Opening billing…" : "Manage billing"}
                  </button>
                </>
              )}
            </div>
            {billingState.error && (
              <p className="mt-3 text-sm text-amber-700" data-testid="carer-hub-manage-billing-error">
                {billingState.error}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Optional, non-blocking Carer Essentials promotion — only shown
          to carers who don't already have it. Never presented as a
          requirement to keep browsing/connecting. */}
      {!hasCarerEssentials && (
        <section
          className="rounded-3xl border border-purple/15 bg-purple/5 p-6 md:p-9"
          data-testid="carer-hub-carer-essentials-promo"
        >
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="w-14 h-14 rounded-2xl bg-purple/10 text-purple flex items-center justify-center shrink-0">
              <FileText className="w-7 h-7" strokeWidth={1.75} />
            </div>
            <div className="flex-1">
              <span className="pill bg-purple/10 text-purple">Optional</span>
              <h3 className="font-display text-xl md:text-2xl font-semibold mt-3">
                Want help keeping your professional admin organised?
              </h3>
              <p className="mt-3 text-ink-muted leading-relaxed">
                Carer Essentials — £7/month. Store your professional documents, track renewals, record CPD, manage
                references and keep your work history organised, all in one private workspace.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to="/membership/carer-essentials"
                  className="btn-purple"
                  data-testid="carer-hub-carer-essentials-join-cta"
                >
                  Explore Carer Essentials — £7/mo <ArrowUpRight className="w-4 h-4" />
                </Link>
                <a
                  href={UPGRADE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline"
                  data-testid="carer-hub-carer-profile-upgrade-cta"
                >
                  Explore Pro Carer Office <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      <StartHereCard
        accent="teal"
        title="Understanding Independent Care"
        body="A clear, plain-English walkthrough of what self-employed care really means, how it compares to agency work, and the common misconceptions worth setting aside. Read it whenever — it isn’t going anywhere."
        href="#guidance"
        ctaLabel="Read the guidance"
        testId="carer-hub-start-here"
      />

      {/* Guidance — long-form articles in an accordion */}
      <section id="guidance" data-testid="carer-hub-guidance" className="scroll-mt-24">
        <div className="max-w-2xl mb-5">
          <h2 className="font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight">
            Guidance &amp; articles
          </h2>
          <p className="mt-1.5 text-ink-muted leading-relaxed">
            Tap a section to read. Take it at your own pace — there’s no rush.
          </p>
        </div>

        <Accordion type="multiple" className="space-y-3" data-testid="carer-hub-guidance-accordion">
          {/* 1. Understanding Independent Care */}
          <AccordionItem
            value="understanding"
            className="rounded-2xl border border-slate-200 bg-white px-5 md:px-7"
          >
            <AccordionTrigger className="py-5 hover:no-underline">
              <span className="flex items-center gap-3 text-left">
                <Compass className="w-5 h-5 text-teal shrink-0" strokeWidth={1.75} />
                <span className="font-display text-lg font-semibold">
                  Understanding Independent Care
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-6 pt-1 text-base leading-relaxed">
              <H3>What self-employed care really means</H3>
              <Para>
                Independent (or self-employed) care means you work directly for the person needing care or their family,
                rather than being employed by an agency. You are still a professional carer — nothing about your standards,
                ethics, or skills changes. What does change is who you answer to and how you are paid.
              </Para>
              <Para>As a self-employed carer, you:</Para>
              <Bullets
                items={[
                  "Agree your terms directly with the client or family",
                  "Set clear expectations around duties, hours and pay",
                  "Invoice for your work",
                  "Keep the full fee for the care you provide",
                ]}
              />
              <Para>
                You are not “on your own” — but you are in control. Independent care is legal, common, and widely used
                across the UK. It simply requires clarity, boundaries, and the right protections in place.
              </Para>

              <H3>The difference between agency work and private care</H3>
              <Para>
                Agency care and independent care can look similar day to day — but structurally, they are very different.
              </Para>
              <Para><strong>With agency work:</strong></Para>
              <Bullets
                items={[
                  "The agency contracts with the family",
                  "The agency sets pay and conditions",
                  "The agency takes a large percentage of the fee",
                  "Carers are often rotated or replaced",
                  "Decisions are made by a third party",
                ]}
              />
              <Para><strong>With independent (private) care:</strong></Para>
              <Bullets
                items={[
                  "You work directly with the family",
                  "You agree your own rate",
                  "You keep 100% of what you earn",
                  "Care is more consistent and personal",
                  "Relationships are built on trust and communication",
                ]}
              />
              <Para>Many carers choose independent care because it allows them to:</Para>
              <Bullets
                items={[
                  "Earn fairly for their work",
                  "Build meaningful, long-term relationships",
                  "Have more control over their working lives",
                ]}
              />
              <Para>
                Neither route is “right” or “wrong” — but they are not the same, and they require different levels of responsibility.
              </Para>

              <H3>Common misconceptions about independent care</H3>
              <Para><strong>“It’s risky or unsafe.”</strong> Independent care is not unsafe when done properly. Clear agreements, good communication and professional boundaries are what make care safe — not an agency logo.</Para>
              <Para><strong>“It’s only for wealthy families.”</strong> In reality, families pay less overall for independent care, while carers earn more. Agency fees inflate costs significantly.</Para>
              <Para><strong>“You have no support.”</strong> Support doesn’t have to come from an agency. Platforms like Just Care Community exist to offer guidance, tools and clear information — without taking a cut of your income. We host communities for both Free and Pro Carers, because shared experience and peer support are gold in this line of work.</Para>
              <Para><strong>“You need to know everything before you start.”</strong> You don’t. Most carers move into independent care gradually, learning as they go and upgrading their protections when needed.</Para>
              <Para><strong>“It’s not professional.”</strong> Professionalism comes from how you work — not who sits in the middle. Independent carers are highly skilled professionals who take responsibility for their practice.</Para>

              <H3>A calm word of reassurance</H3>
              <Para>
                Independent care isn’t about rushing, risking, or doing everything at once. It’s about understanding your
                options, making informed choices and taking responsibility step by step. You don’t have to decide anything
                today. You’re here to learn — and that’s exactly where you should be.
              </Para>
            </AccordionContent>
          </AccordionItem>

          {/* 2. Getting Paid & Staying Safe */}
          <AccordionItem
            value="getting-paid"
            className="rounded-2xl border border-slate-200 bg-white px-5 md:px-7"
          >
            <AccordionTrigger className="py-5 hover:no-underline">
              <span className="flex items-center gap-3 text-left">
                <Coins className="w-5 h-5 text-teal shrink-0" strokeWidth={1.75} />
                <span className="font-display text-lg font-semibold">
                  Getting Paid &amp; Staying Safe
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-6 pt-1 text-base leading-relaxed">
              <Para>
                Working independently gives you freedom — but it also means you need to protect yourself properly when it
                comes to getting paid. This isn’t about being “formal” or difficult. It’s about making sure everyone
                understands what’s agreed — so there are no problems later.
              </Para>

              <H3>Payment basics for independent carers</H3>
              <Para>As a self-employed carer, you are responsible for setting your own rates and payment terms. That includes:</Para>
              <Bullets
                items={[
                  "Your hourly or daily rate",
                  "When you expect to be paid (weekly, fortnightly, etc.)",
                  "How you are paid (bank transfer, cash, etc.)",
                  "Any extras (travel time, nights, bank holidays)",
                ]}
              />
              <Para>
                Clear pricing isn’t just professional — it avoids awkward conversations later. If something isn’t agreed
                upfront, it often becomes a problem later.
              </Para>

              <H3>Who is legally responsible for paying you</H3>
              <Para>This is one of the most misunderstood areas. The person receiving care is not always the one responsible for payment. It could be:</Para>
              <Bullets
                items={[
                  "A family member",
                  "A power of attorney",
                  "A deputy or representative",
                  "A direct payment holder",
                ]}
              />
              <Para>
                What matters is this: you need to know exactly who has agreed to pay you — and that they have the authority to do so.
                If this isn’t clear from the start, you risk late payments, disputes between family members, or not being paid at all.
              </Para>

              <H3>Why verbal agreements cause problems</H3>
              <Para>Many carers start with a simple conversation and a handshake. And most of the time… it works — until it doesn’t. Verbal agreements can lead to:</Para>
              <Bullets
                items={[
                  "“That’s not what we agreed” conversations",
                  "Confusion over hours, duties or pay",
                  "Family members stepping in and changing terms",
                  "Difficulty proving anything if a dispute happens",
                ]}
              />
              <Para>It’s not about trust — it’s about clarity. Even good people remember things differently.</Para>

              <H3>When contracts become essential</H3>
              <Para>You don’t need a complicated legal document to start working, but you do need something written once:</Para>
              <Bullets
                items={[
                  "The role becomes ongoing",
                  "There are regular hours or live-in arrangements",
                  "Payment is significant or long-term",
                  "Multiple family members are involved",
                ]}
              />
              <Para>A simple written agreement protects everyone. It should clearly cover:</Para>
              <Bullets
                items={[
                  "Your rate and payment schedule",
                  "Your role and responsibilities",
                  "Notice periods",
                  "What happens if things change",
                ]}
              />
              <Para>Think of it as a shared understanding — not a legal battle.</Para>

              <H3>Why this matters</H3>
              <Para>Independent care works best when it’s clear, fair and professional from day one. Setting expectations early:</Para>
              <Bullets
                items={[
                  "Builds trust",
                  "Prevents awkward situations",
                  "Protects your income",
                  "Helps families feel confident in you",
                ]}
              />
              <Para>You’re not “just helping out” — you’re providing a skilled, professional service.</Para>
            </AccordionContent>
          </AccordionItem>

          {/* 3. Things to Consider */}
          <AccordionItem
            value="things-to-consider"
            className="rounded-2xl border border-slate-200 bg-white px-5 md:px-7"
          >
            <AccordionTrigger className="py-5 hover:no-underline">
              <span className="flex items-center gap-3 text-left">
                <ClipboardCheck className="w-5 h-5 text-teal shrink-0" strokeWidth={1.75} />
                <span className="font-display text-lg font-semibold">
                  Things to Consider Before Becoming an Independent Carer
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-6 pt-1 text-base leading-relaxed">
              <H3>Take your time — independence doesn’t have to be rushed</H3>
              <Para>Moving into private, independent care is not something you need to do overnight. Many carers:</Para>
              <Bullets
                items={[
                  "Start with one private client",
                  "Combine agency and private work",
                  "Take time to understand what feels right",
                ]}
              />
              <Para>There is no “correct” speed. The most important thing is being informed and prepared.</Para>

              <H3>Insurance and basic protections</H3>
              <Para>Before taking private clients, you should understand:</Para>
              <Bullets
                items={[
                  "Whether you need public liability insurance",
                  "Whether professional indemnity cover is appropriate",
                  "What your insurance does — and does not — cover",
                ]}
              />
              <Para>
                Insurance doesn’t need to be complicated or expensive, but it is an important foundation. If something goes
                wrong, you need to know where you stand.
              </Para>

              <H3>Setting boundaries and expectations</H3>
              <Para>Independent care works best when expectations are clear from the start. Before agreeing to care, consider:</Para>
              <Bullets
                items={[
                  "What tasks you are happy to take on",
                  "What tasks you are not comfortable with",
                  "Your working hours and breaks",
                  "How overtime or changes will be handled",
                ]}
              />
              <Para>Clear boundaries protect your wellbeing, the quality of care and the working relationship. Saying “no” is not unkind — it is professional.</Para>

              <H3>Red flags to be aware of</H3>
              <Para>Most families have good intentions, but some situations deserve caution. Be mindful if:</Para>
              <Bullets
                items={[
                  "Payment details are vague or delayed",
                  "Funding is “expected” but not confirmed",
                  "Responsibilities keep changing without agreement",
                  "You are pressured to start before terms are clear",
                ]}
              />
              <Para>If something doesn’t feel right, it’s worth pausing and asking questions.</Para>

              <H3>Understanding your responsibilities</H3>
              <Para>Independent care brings greater control — but also greater responsibility. This includes:</Para>
              <Bullets
                items={[
                  "Keeping records (even basic ones are better than none)",
                  "Communicating clearly",
                  "Knowing when to ask for advice",
                  "Protecting yourself financially",
                ]}
              />
              <Para>You are not expected to be perfect — just informed.</Para>

              <H3>When extra support becomes important</H3>
              <Para>As care arrangements become longer-term, higher value or more complex, additional protections and tools become more important. This is usually the point where carers choose to:</Para>
              <Bullets
                items={[
                  "Use contracts",
                  "Formalise agreements",
                  "Access professional templates and guidance",
                ]}
              />
              <Para>There’s no pressure to do this immediately — but understanding when it matters is key.</Para>

              <H3>A final word of reassurance</H3>
              <Para>Independent care is not about taking risks or being left alone. It’s about making informed choices, working with clarity and confidence, and building care relationships that are fair and sustainable. You don’t have to have everything in place before you begin — but you do deserve to understand your options.</Para>

              <H3>What comes next</H3>
              <Para>
                If you’re thinking about moving further into independent care, the next step is usually understanding how to
                protect your income and formalise arrangements. That’s where Pro tools and templates become useful — when
                you decide the time is right.
              </Para>
            </AccordionContent>
          </AccordionItem>

          {/* 4. When to Upgrade */}
          <AccordionItem
            value="when-to-upgrade"
            className="rounded-2xl border border-slate-200 bg-white px-5 md:px-7"
          >
            <AccordionTrigger className="py-5 hover:no-underline">
              <span className="flex items-center gap-3 text-left">
                <Sparkles className="w-5 h-5 text-purple shrink-0" strokeWidth={1.75} />
                <span className="font-display text-lg font-semibold">
                  When to Consider Pro Carer Membership
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-6 pt-1 text-base leading-relaxed">
              <Para>
                Upgrading to Pro Carer membership isn’t about being “more serious” or doing things faster. It’s about
                timing. Many carers stay on the Free tier while they learn, explore and build confidence. Others choose
                to upgrade when their work — or their risk — starts to increase. There’s no right or wrong moment.
              </Para>

              <H3>Situations where Pro tools become important</H3>
              <Para>Carers often choose to upgrade when:</Para>
              <Bullets
                items={[
                  "They take on private clients regularly",
                  "They provide respite or live-in care",
                  "The value of the care provided increases",
                  "Payment disputes start to feel like a real risk",
                  "They want clearer boundaries and written agreements",
                  "They want to look and operate more professionally",
                ]}
              />
              <Para>At this stage, good intentions are no longer enough — structure matters.</Para>

              <H3>What Pro Carer membership gives you</H3>
              <Para>Pro Carer membership within Just Care Community is designed to support carers who are actively working independently. It provides access to:</Para>
              <Bullets
                items={[
                  "Professional contracts and agreements",
                  "Payment protection tools and guidance",
                  "Professional templates, forms and practical resources for bookings, deposits, invoices and more",
                  "Clear frameworks that reduce misunderstandings",
                  "Practical tools that protect your income and time",
                ]}
              />
              <Para>These tools aren’t about bureaucracy — they’re about peace of mind.</Para>

              <H3>Why these tools aren’t included for free</H3>
              <Para>Contracts, payment structures and protection tools carry responsibility. They need to be used correctly, understood properly and applied at the right time. Pro membership ensures carers using these tools:</Para>
              <Bullets
                items={[
                  "Have the right context",
                  "Are working professionally",
                  "Are better protected if issues arise",
                ]}
              />
              <Para>This keeps the platform ethical, safe and sustainable.</Para>

              <H3>No pressure, no urgency</H3>
              <Para>You will never be pressured to upgrade. Many carers stay Free while learning, upgrade later when circumstances change, or move between agency and private work over time. You are always in control.</Para>

              <H3>A calm way to think about it</H3>
              <Bullets
                items={[
                  "Free Carer → Knowledge, understanding, confidence",
                  "Pro Carer → Tools, protection, structure",
                ]}
              />
              <Para>Both are valid. Both have a place.</Para>

              <div className="mt-6">
                <a
                  href={UPGRADE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-purple"
                  data-testid="upgrade-inline"
                >
                  Upgrade to Pro Carer <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      {/* Starter Templates */}
      <section id="templates" data-testid="carer-hub-templates" className="scroll-mt-24">
        <div className="max-w-2xl mb-5">
          <h2 className="font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight">
            Your starter templates
          </h2>
          <p className="mt-1.5 text-ink-muted leading-relaxed">
            These are your starter tools. They aren’t complicated, they aren’t overwhelming — but they are the difference
            between working casually and working professionally. When you’re ready for deeper protection, more detailed
            templates and advanced guidance, the Pro Carer Office is there for you.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TEMPLATES.map((t) => (
            <article
              key={t.n}
              className="card-base p-6 flex flex-col"
              data-testid={`template-card-${t.n}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl bg-teal/10 text-teal flex items-center justify-center">
                  <Download className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <span className="pill bg-teal/10 text-teal">Template {t.n}</span>
              </div>
              <h3 className="font-display text-lg font-semibold leading-snug">
                {t.title}
              </h3>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">
                {t.body}
              </p>
              {t.internal ? (
                <Link
                  to={t.href}
                  className="btn-teal mt-5 !py-2.5 !px-4 text-sm"
                  data-testid={`template-download-${t.n}`}
                >
                  {t.cta}
                  <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
                </Link>
              ) : (
                <a
                  href={t.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-teal mt-5 !py-2.5 !px-4 text-sm"
                  data-testid={`template-download-${t.n}`}
                >
                  {t.cta}
                  <ExternalLink className="w-4 h-4" strokeWidth={1.75} />
                </a>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* Training Hub */}
      <section id="training" data-testid="carer-hub-training" className="scroll-mt-24">
        <div className="max-w-2xl mb-5">
          <div className="overline text-teal mb-2 flex items-center gap-2">
            <GraduationCap className="w-4 h-4" strokeWidth={2} /> Training hub
          </div>
          <h2 className="font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight">
            What training is commonly expected
          </h2>
          <p className="mt-1.5 text-ink-muted leading-relaxed">
            There is no single legal “training list” for self-employed carers — but in practice, families, councils,
            insurers and professionals expect carers to hold up-to-date core training relevant to the care being provided.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Core training */}
          <div className="card-base p-6 lg:col-span-1">
            <H3>Core training commonly expected</H3>
            <Bullets items={CORE_TRAINING} />
          </div>

          {/* Why it matters / notes */}
          <div className="card-base p-6 lg:col-span-2 space-y-5">
            <div>
              <div className="flex items-center gap-2 text-teal font-semibold">
                <Info className="w-4 h-4" strokeWidth={2} /> Why this matters
              </div>
              <Para>
                Families may ask about training before confirming a booking, or later if care needs change. Having
                certificates ready helps build trust quickly, avoid awkward conversations, support Direct Payments
                requirements and protect you professionally.
              </Para>
            </div>
            <div>
              <div className="flex items-center gap-2 text-amber-700 font-semibold">
                <AlertTriangle className="w-4 h-4" strokeWidth={2} /> An important note
              </div>
              <Para>
                Training does not replace experience — but it does show commitment to safe practice and ongoing
                professional development. Some clients may require additional or specialist training depending on their
                needs (for example: Parkinson’s, stroke care, or complex dementia).
              </Para>
            </div>
            <div>
              <div className="flex items-center gap-2 text-ink font-semibold">
                <Compass className="w-4 h-4" strokeWidth={2} /> Who decides what training you need?
              </div>
              <Para>
                Training requirements are agreed between you (the carer), the client or family, and any relevant funding
                body or insurer. JCC cannot determine individual training requirements but can guide you to trusted
                accredited providers.
              </Para>
              <Para>
                If you are unsure what training is “enough”, you are not alone. Expectations vary — and many experienced
                carers add or refresh training gradually over time.
              </Para>
            </div>
          </div>
        </div>
      </section>

      {/* How training works */}
      <section id="how-training-works" data-testid="carer-hub-training-steps" className="scroll-mt-24">
        <div className="max-w-2xl mb-5">
          <h2 className="font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight">
            How training works
          </h2>
          <p className="mt-1.5 text-ink-muted leading-relaxed">
            Completing care training is usually straightforward and can be done online, at your own pace.
          </p>
        </div>
        <ol className="grid md:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="training-steps">
          {TRAINING_STEPS.map((s) => (
            <li key={s.n} className="card-base p-5 flex flex-col">
              <div className="w-9 h-9 rounded-full bg-teal text-white flex items-center justify-center font-semibold text-sm">
                {s.n}
              </div>
              <h3 className="font-display text-base font-semibold mt-4">{s.title}</h3>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>
        <div className="mt-5 rounded-2xl bg-teal/5 border border-teal/15 p-5 text-sm text-ink-muted leading-relaxed">
          <strong className="text-ink">Refreshing training:</strong> many carers refresh core training every 12–24 months,
          or sooner if care needs change, a family requests updated evidence, or you want to specialise or increase your rates.
          You do not need to complete everything at once.
        </div>
      </section>

      {/* Providers */}
      <section id="providers" data-testid="carer-hub-providers" className="scroll-mt-24">
        <div className="max-w-2xl mb-5">
          <h2 className="font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight">
            Recommended training providers
          </h2>
          <p className="mt-1.5 text-ink-muted leading-relaxed">
            JCC does not deliver training or certification. The providers below are independent, accredited organisations
            commonly used across the UK care sector. They offer flexible online learning suitable for self-employed carers.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROVIDERS.map((p) => (
            <article key={p.name} className="card-base p-6" data-testid={`provider-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <h3 className="font-display text-lg font-semibold">{p.name}</h3>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed">{p.body}</p>
            </article>
          ))}
        </div>
        <div className="mt-5 grid md:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-5 text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">Choosing the right provider.</strong> There is no single “correct” provider.
            Carers usually choose based on cost, course availability, ease of access, and client or council expectations.
            Any accredited provider offering the appropriate course content is generally acceptable.
          </div>
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5 text-sm text-amber-900 leading-relaxed">
            <strong>Important note.</strong> JCC provides information only. All training is completed directly with the
            provider, who is responsible for course content, certification and accreditation.
          </div>
        </div>
      </section>

      {/* New Carer Starter Path */}
      <section id="starter-path" data-testid="carer-hub-starter-path" className="scroll-mt-24">
        <div className="max-w-2xl mb-5">
          <h2 className="font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight">
            New Carer Starter Path
          </h2>
          <p className="mt-1.5 text-ink-muted leading-relaxed">
            If you’re new to care — or newly working independently — it’s normal to feel unsure about where to start with
            training. This simple starter path is designed to help you build confidence step by step, without overwhelm.
          </p>
        </div>
        <ol className="space-y-4" data-testid="starter-path-steps">
          {STARTER_PATH.map((s) => (
            <li
              key={s.n}
              className="card-base p-5 md:p-6 flex gap-4 md:gap-5 items-start"
            >
              <div className="w-9 h-9 rounded-full bg-purple text-white flex items-center justify-center font-semibold text-sm shrink-0">
                {s.n}
              </div>
              <div>
                <h3 className="font-display text-base md:text-lg font-semibold">{s.title}</h3>
                <p className="mt-1.5 text-sm md:text-base text-ink-muted leading-relaxed">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-5 rounded-2xl bg-purple/5 border border-purple/15 p-5 text-sm text-ink-muted leading-relaxed">
          <strong className="text-ink">Reassurance for new carers.</strong> Feeling unsure does not mean you are
          unprepared. Most good carers build their confidence through experience, reflection and ongoing learning.
          Training supports your practice — it does not define your worth as a carer.
        </div>
      </section>

      {/* Upgrade CTA */}
      <WorkspaceSection
        testId="carer-hub-next"
        title="When you’re ready to go further"
        description="Upgrading is optional and entirely your choice. No pressure, no urgency."
      >
        <WorkspaceCard
          accent="purple"
          icon={Sparkles}
          title="Upgrade to Pro Carer Office"
          body="Full contracts & templates, placement protection tools, payment-security resources, professional working tools, and priority updates and support."
          href={UPGRADE_URL}
          external
          status="external"
          ctaLabel="Upgrade to Pro Carer"
          testId="upgrade-card"
        />
      </WorkspaceSection>

      <WorkspaceSupportBlock />

      {/* Legal disclaimer */}
      <section data-testid="carer-hub-disclaimer" className="rounded-3xl border border-slate-200 bg-[#f7f7f3] p-6 md:p-8">
        <h3 className="font-display text-base font-semibold text-ink">JCC Legal Disclaimer</h3>
        <div className="mt-3 space-y-3 text-xs md:text-sm text-ink-muted leading-relaxed">
          <p>
            Just Care Community (“JCC”) provides practical information, educational resources, templates, peer support
            and digital tools for self-employed carers and families arranging care privately.
          </p>
          <p>
            JCC is not a care agency, care provider, employment agency, legal practice, accountancy service, medical
            service, safeguarding authority or emergency service. JCC does not recruit, employ, introduce, vet,
            supervise, manage, direct or monitor carers, and does not assess or guarantee the suitability, qualifications,
            insurance, availability, conduct or performance of any carer, family member or other user.
          </p>
          <p>
            Any care arrangement, contract, payment arrangement, working arrangement or decision to work together is made
            directly between the individuals involved. Users remain fully responsible for carrying out appropriate checks,
            including identity, references, DBS status where relevant, right-to-work status, insurance, training,
            employment status, safeguarding considerations and compliance with any applicable laws or regulations.
          </p>
          <p>
            JCC templates and resources are provided as general guidance only. They are not a substitute for independent
            legal, tax, employment, financial, medical, clinical, safeguarding or professional advice. Laws and individual
            circumstances vary, and users should seek suitably qualified advice where needed.
          </p>
          <p>
            Information shared within JCC, including in community areas and Private Care Partnership Rooms, should not be
            treated as professional advice or relied upon as the sole basis for decisions affecting care, safety,
            employment, finances or legal rights. In an emergency, contact the appropriate emergency service or
            healthcare professional without delay.
          </p>
          <p>
            While JCC aims to provide useful, accurate and up-to-date resources, no guarantee is made that content will be
            complete, current, suitable for every situation or free from error. To the fullest extent permitted by law,
            JCC accepts no liability for loss, damage, injury, dispute or claim arising from reliance on JCC content,
            templates, tools, user-generated content or arrangements made between users.
          </p>
          <p>Nothing in this disclaimer excludes or limits liability where it cannot legally be excluded or limited.</p>
        </div>
      </section>
    </WorkspaceShell>
  );
}
